import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Building2, Pencil, Trash2, RefreshCw, Loader2, Info, FileText, ListTodo,
  AlertTriangle, Ticket, Flame, ClipboardList, FireExtinguisher, FileDown, Layers,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';
import StatusDot from '@/components/StatusDot';
import ViolationsTab from '@/pages/ViolationsTab';
import DotPermitsTab from '@/pages/DotPermitsTab';
import DotBatchTrackers from '@/components/DotBatchTrackers';
import { DobFilings } from '@/components/DobFilings';
import { FdnyInspections } from '@/components/FdnyInspections';
import FdnyPermits from '@/components/FdnyPermits';
import { DocumentsSection } from '@/components/documents/DocumentsSection';
import { BulkPullReview } from '@/components/BulkPullReview';

const BOROUGHS = ['Manhattan', 'Bronx', 'Brooklyn', 'Queens', 'Staten Island'];
const LABEL_STYLE = { fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#031333' };

const EMPTY_EDIT = {
  display_name: '', address: '', borough: '', bin: '', block: '', lot: '',
  client_id: '', project_type: '', notes: '',
};

const TABS = [
  ['summary', 'Summary', FileText],
  ['violations', 'Violations', AlertTriangle],
  ['dot-permits', 'DOT Permits', Ticket],
  ['dot-batch', 'DOT Batches', Layers],
  ['dob-filings', 'DOB Filings', Building2],
  ['fdny', 'FDNY Inspections', Flame],
  ['fdny-permits', 'FDNY Permits', FireExtinguisher],
  ['documents', 'Documents', FileText],
  ['projects', 'Projects', ClipboardList],
];

export default function PropertyProfilePage() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab = TABS.some(t => t[0] === rawTab) ? rawTab : 'summary';

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [clients, setClients] = useState([]);
  const [linkedProjects, setLinkedProjects] = useState([]);

  const [bisLoading, setBisLoading] = useState(false);
  const [bisNoData, setBisNoData] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [savingEdit, setSavingEdit] = useState(false);

  // Bulk pull review (BIN discover) state.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkData, setBulkData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [pulling, setPulling] = useState(false);

  const exportCompliancePdf = async () => {
    setExportingPdf(true);
    try {
      const r = await api.get(`/properties/${id}/compliance-report-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      const nm = (property?.display_name || property?.address || 'property').replace(/\s+/g, '_');
      a.href = url; a.download = `Compliance_Report_${nm}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not generate the compliance report');
    }
    setExportingPdf(false);
  };

  const runBulkDiscover = async ({ silent = false } = {}) => {
    if (!silent) {
      const ok = property?.bin?.trim() && property?.block?.trim() && property?.lot?.trim();
      if (!ok) { toast.info('Add BIN, Block, and Lot to this property to run the NYC Open Data pull'); return; }
    }
    setPulling(true);
    try {
      const r = await api.post(`/properties/${id}/bulk-discover`);
      if ((r.data?.total || 0) > 0) { setBulkData(r.data); setBulkOpen(true); }
      else if (!silent) { toast.info('No new records found on NYC Open Data for this BIN'); }
    } catch {
      if (!silent) toast.error('Could not reach NYC Open Data for the bulk pull. Please try again in a moment.');
    }
    setPulling(false);
  };

  const loadProperty = useCallback(async () => {
    try {
      const r = await api.get(`/properties/${id}`);
      setProperty(r.data);
    } catch (e) {
      setNotFound(true);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadProperty();
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get(`/properties/${id}/projects`).then(r => setLinkedProjects(Array.isArray(r.data) ? r.data : [])).catch(() => setLinkedProjects([]));
  }, [loadProperty, id]);

  const changeTab = (t) => {
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', t); return p; });
  };

  const runBisLookup = async () => {
    setBisLoading(true);
    try {
      const r = await api.post(`/properties/${id}/bis-lookup`);
      setProperty(r.data);
      if (r.data.warning) {
        setBisNoData(true);
        toast.warning(r.data.warning);
      } else {
        setBisNoData(false);
        toast.success('Building data loaded');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Building profile lookup failed'); }
    setBisLoading(false);
  };

  const openEdit = () => {
    setEditForm({
      display_name: property.display_name || '', address: property.address || '', borough: property.borough || '',
      bin: property.bin || '', block: property.block || '', lot: property.lot || '',
      client_id: property.client_id || '', project_type: property.project_type || '', notes: property.notes || '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.address.trim() || !editForm.borough) return toast.error('Address and Borough are required');
    setSavingEdit(true);
    try {
      const r = await api.put(`/properties/${id}`, { ...editForm, client_id: editForm.client_id || null });
      setProperty(r.data);
      toast.success('Property updated');
      setEditOpen(false);
      if (editForm.bin?.trim() && editForm.block?.trim() && editForm.lot?.trim()) runBulkDiscover({ silent: true });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating property'); }
    setSavingEdit(false);
  };

  const deleteProperty = async () => {
    const ok = await confirm({ title: 'Delete property', description: 'Delete this property? This cannot be undone.', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success('Property deleted');
      navigate('/properties');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error deleting property'); }
  };

  if (loading) return <div className="animate-pulse text-muted-foreground py-8">Loading property...</div>;
  if (notFound || !property) return (
    <div className="space-y-4 py-8" data-testid="property-not-found">
      <button onClick={() => navigate('/properties')} className="hover:underline" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#1B4A76' }} data-testid="property-breadcrumb-back">Properties</button>
      <p className="text-sm text-muted-foreground">Property not found.</p>
    </div>
  );

  const bis = property.bis_data;
  const bisFields = [
    ['Building Class', bis?.building_class],
    ['Zoning District', bis?.zoning_district],
    ['Stories', bis?.stories],
    ['Year Built', bis?.year_built],
    ['Gross Sq Ft', bis?.gross_sq_ft],
    ['Open Complaints', bis?.open_complaints],
  ];

  return (
    <div className="space-y-4 animate-fade-in" data-testid="property-profile-page">
      {/* Breadcrumb */}
      <button onClick={() => navigate('/properties')} className="hover:underline" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#1B4A76' }} data-testid="property-breadcrumb-back">
        ← Properties
      </button>

      {/* Header card */}
      <div className="rounded bg-card flex items-start justify-between gap-4 flex-wrap" style={{ border: '1px solid #D4DCE8', padding: '20px' }} data-testid="property-header-card">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4" style={{ color: '#1B4A76' }} />
            <h1 className="font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '18px', color: '#010A21' }} data-testid="property-header-name">{property.display_name || property.address}</h1>
          </div>
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1">
            {property.address && <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#031333' }}>{property.address}</span>}
            {property.borough && <><span style={{ color: '#031333' }}>·</span><span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#031333' }}>{property.borough}</span></>}
            {property.bin && <><span style={{ color: '#031333' }}>·</span><span style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px', color: '#031333' }}>BIN {property.bin}</span></>}
            {property.client_name && <><span style={{ color: '#031333' }}>·</span><span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#031333' }}>{property.client_name}</span></>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline" className="border-border" onClick={() => runBulkDiscover()} disabled={pulling} data-testid="property-repull-btn">
            {pulling ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Pull NYC Open Data
          </Button>
          <Button size="sm" variant="outline" className="border-border" onClick={exportCompliancePdf} disabled={exportingPdf} data-testid="property-compliance-pdf-btn">
            {exportingPdf ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1" />} Compliance PDF
          </Button>
          <Button size="sm" variant="outline" className="border-border" onClick={openEdit} data-testid="property-edit-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive border-border" onClick={deleteProperty} data-testid="property-delete-btn"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
        </div>
      </div>

      {/* BIS Data Strip */}
      <div className="rounded bg-card" style={{ border: '1px solid #D4DCE8', padding: '16px' }} data-testid="property-bis-strip">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold text-foreground">Building Profile</p>
            <TooltipProvider delayDuration={100}><Tooltip><TooltipTrigger asChild><Info className="h-3.5 w-3.5 cursor-help" style={{ color: '#031333' }} /></TooltipTrigger><TooltipContent className="max-w-[240px] border-0 text-sm" style={{ backgroundColor: '#132C4A', color: '#fff', borderRadius: '4px' }}>Load building data (class, zoning, stories, year built, floor area) from NYC MapPLUTO for this property.</TooltipContent></Tooltip></TooltipProvider>
            <span className="text-xs text-muted-foreground ml-2">{bis?.fetched_at ? `Last fetched ${new Date(bis.fetched_at).toLocaleString()}` : 'No data fetched yet'}</span>
          </div>
          <Button size="sm" variant="outline" onClick={runBisLookup} disabled={bisLoading} className="border-border" data-testid="bis-lookup-btn">
            {bisLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            {bisLoading ? 'Loading...' : (bis ? 'Refresh' : 'Load Building Profile')}
          </Button>
        </div>
        {bis && (
          <div className="flex flex-wrap gap-x-8 gap-y-2 mt-3 pt-3 border-t border-border" data-testid="property-bis-data">
            {bisFields.map(([label, val]) => (
              <p key={label} className="text-sm">
                <span style={{ color: '#031333' }}>{label}: </span>
                <span style={{ fontFamily: "'DM Mono', monospace", color: '#010A21' }}>{val || '\u00A0'}</span>
              </p>
            ))}
          </div>
        )}
        {!bis && bisNoData && (
          <p className="mt-3 pt-3 border-t border-border text-left" style={{ fontSize: '12px', color: '#031333' }} data-testid="property-bis-nodata">
            No building data found for this property. Verify the block and lot are correct and try again.
          </p>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent p-0 h-auto flex-wrap" data-testid="property-profile-tabs">
          {TABS.map(([val, label, Icon]) => (
            <TabsTrigger key={val} value={val} className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-xs data-[state=active]:border-[#E5441A] data-[state=active]:bg-transparent data-[state=active]:font-medium data-[state=active]:shadow-none" data-testid={`property-tab-${val}`}>
              <Icon className="h-3 w-3 mr-1" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Summary */}
        <TabsContent value="summary" className="mt-4 space-y-4" data-testid="property-summary-tab">
          <div className="rounded bg-card flex flex-wrap gap-x-8 gap-y-2" style={{ border: '1px solid #D4DCE8', padding: '16px' }}>
            {[
              ['Display Name', property.display_name, false],
              ['Address', property.address, false],
              ['Borough', property.borough, false],
              ['BIN', property.bin, true],
              ['Block', property.block, true],
              ['Lot', property.lot, true],
              ['Type', property.project_type, false],
              ['Client', property.client_name, false],
            ].map(([label, val, mono]) => (
              <p key={label} className="text-sm">
                <span style={{ color: '#031333' }}>{label}: </span>
                <span style={{ color: '#010A21', fontFamily: mono ? "'DM Mono', monospace" : undefined }}>{val || '\u00A0'}</span>
              </p>
            ))}
          </div>
          {property.notes && (
            <div className="rounded bg-card" style={{ border: '1px solid #D4DCE8', padding: '16px' }}>
              <p style={LABEL_STYLE} className="mb-1">Notes</p>
              <p className="text-sm whitespace-pre-wrap" style={{ color: '#010A21' }} data-testid="property-notes">{property.notes}</p>
            </div>
          )}
        </TabsContent>

        {/* Violations */}
        <TabsContent value="violations" className="mt-4"><ViolationsTab key={`viol-${refreshKey}`} lockedPropertyId={id} /></TabsContent>

        {/* DOT Permits */}
        <TabsContent value="dot-permits" className="mt-4"><DotPermitsTab lockedPropertyId={id} /></TabsContent>

        {/* DOT Batch Trackers */}
        <TabsContent value="dot-batch" className="mt-4"><DotBatchTrackers lockedPropertyId={id} /></TabsContent>

        {/* DOB Filings */}
        <TabsContent value="dob-filings" className="mt-4"><DobFilings key={`dob-${refreshKey}`} scope="property" propertyId={id} onGoToProjects={() => changeTab('projects')} /></TabsContent>

        {/* FDNY Inspections */}
        <TabsContent value="fdny" className="mt-4"><FdnyInspections propertyId={id} /></TabsContent>

        {/* FDNY Permits */}
        <TabsContent value="fdny-permits" className="mt-4"><FdnyPermits lockedPropertyId={id} /></TabsContent>

        {/* Documents */}
        <TabsContent value="documents" className="mt-4"><DocumentsSection recordType="Property" recordId={id} contextPropertyId={id} /></TabsContent>

        {/* Projects */}
        <TabsContent value="projects" className="mt-4 space-y-2" data-testid="property-projects-tab">
          {(Array.isArray(linkedProjects) ? linkedProjects : []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-4" data-testid="property-projects-empty">No projects linked to this property yet.</p>
          ) : (
            <div className="border border-border rounded-md bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-xs text-muted-foreground">Number</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Name</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Client</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Status</TableHead>
                    <TableHead className="text-xs text-muted-foreground text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linkedProjects.map(p => (
                    <TableRow key={p.id} className="border-border hover:bg-secondary/30 cursor-pointer" onClick={() => navigate(`/projects/${p.id}`)} data-testid={`property-project-row-${p.id}`}>
                      <TableCell className="text-sm font-mono">{p.project_number}</TableCell>
                      <TableCell className="text-sm">{p.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{p.client_name}</TableCell>
                      <TableCell><StatusDot status={p.status} /></TableCell>
                      <TableCell className="text-right"><button onClick={(e) => { e.stopPropagation(); navigate(`/projects/${p.id}`); }} className="text-sm text-[#1B4A76] hover:underline" data-testid={`property-project-view-${p.id}`}>View</button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Property Sheet */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="sm:max-w-md bg-card border-border overflow-y-auto" data-testid="property-edit-sheet">
          <SheetHeader>
            <SheetTitle style={{ color: '#010A21' }}>Edit Property</SheetTitle>
            <SheetDescription>Update property details.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs text-muted-foreground">Display Name</Label><Input value={editForm.display_name} onChange={e => setEditForm({ ...editForm, display_name: e.target.value })} className="bg-background border-border" data-testid="property-edit-display-name" /></div>
            <div><Label className="text-xs text-muted-foreground">Address *</Label><Input value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} className="bg-background border-border" data-testid="property-edit-address" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">Borough *</Label>
                <Select value={editForm.borough || '__none'} onValueChange={v => setEditForm({ ...editForm, borough: v === '__none' ? '' : v })}>
                  <SelectTrigger className="bg-background border-border" data-testid="property-edit-borough"><SelectValue placeholder="Select borough" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">{BOROUGHS.map(b => <SelectItem key={b} value={b} className="text-sm">{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-muted-foreground">Type</Label><Input value={editForm.project_type} onChange={e => setEditForm({ ...editForm, project_type: e.target.value })} placeholder="e.g. New Building" className="bg-background border-border" data-testid="property-edit-type" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs text-muted-foreground">BIN</Label><Input value={editForm.bin} onChange={e => setEditForm({ ...editForm, bin: e.target.value })} className="bg-background border-border" data-testid="property-edit-bin" /></div>
              <div><Label className="text-xs text-muted-foreground">Block</Label><Input value={editForm.block} onChange={e => setEditForm({ ...editForm, block: e.target.value })} className="bg-background border-border" data-testid="property-edit-block" /></div>
              <div><Label className="text-xs text-muted-foreground">Lot</Label><Input value={editForm.lot} onChange={e => setEditForm({ ...editForm, lot: e.target.value })} className="bg-background border-border" data-testid="property-edit-lot" /></div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Client</Label>
              <Select value={editForm.client_id || '__none'} onValueChange={v => setEditForm({ ...editForm, client_id: v === '__none' ? '' : v })}>
                <SelectTrigger className="bg-background border-border" data-testid="property-edit-client"><SelectValue placeholder="No client" /></SelectTrigger>
                <SelectContent className="bg-card border-border max-h-64">
                  <SelectItem value="__none" className="text-sm text-muted-foreground">No client</SelectItem>
                  {(Array.isArray(clients) ? clients : []).map(c => <SelectItem key={c.id} value={c.id} className="text-sm">{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-muted-foreground">Notes</Label><Textarea value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} rows={3} className="bg-background border-border" data-testid="property-edit-notes" /></div>
            <div className="flex gap-2 pt-1">
              <Button onClick={saveEdit} disabled={savingEdit} className="bg-primary text-primary-foreground" data-testid="property-edit-save">{savingEdit ? 'Saving...' : 'Save Changes'}</Button>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Bulk pull review (BIN discover) */}
      <BulkPullReview
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        propertyId={id}
        data={bulkData}
        onAdded={() => { loadProperty(); setRefreshKey(k => k + 1); }}
      />
    </div>
  );
}
